/**
 * @module ISO27001Context
 * @description Contexto global para la gestión del Sistema de Gestión de Seguridad
 * de la Información (SGSI) según ISO 27001:2022.
 *
 * Administra los requisitos del Anexo A de la norma ISO 27001:2022, organizados
 * en cláusulas (4-10 de la norma), con soporte completo para:
 * - Seguimiento del estado de cumplimiento por requisito.
 * - Asignación de referentes técnicos responsables.
 * - Gestión de tareas y evidencias por requisito.
 * - Historial de auditoría de cambios.
 * - Métricas calculadas en tiempo real por cláusula y global.
 *
 * IMPORTANTE: Este contexto es completamente independiente de `DataContext` (ISO 27002).
 * ISO 27001 gestiona el SGSI (qué hacer), mientras ISO 27002 gestiona los controles
 * técnicos (cómo hacerlo).
 *
 * Estrategia de carga:
 * - Intenta cargar desde tablas `iso27001_*` en Supabase.
 * - Si las tablas no existen (código PGRST205 / 42P01), no cae a mock — retorna vacío.
 * - Solo usa mock cuando el usuario no está autenticado (modo demo).
 * - Errores de tablas no existentes se manejan con gracia para no bloquear la app.
 *
 * @author Sistema ACSGC
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import { useAuth } from './AuthContext';
import {
  mockISO27001Requirements,
  getISO27001ClausesWithRequirements,
  type ISO27001Clause,
  type ISO27001Requirement,
  type ISO27001Task,
  type ISO27001Evidence,
  type ISO27001AuditEntry,
  type ISO27001Metrics
} from '../data/iso27001MockData';
import type { TechnicalReferent } from '../types/iso';
import { mockTechnicalReferents } from '../data/mockData';

// =====================================================
// INTERFACES
// =====================================================

/**
 * Contrato del contexto ISO 27001:2022.
 *
 * @property clauses - Cláusulas de la norma (4-10) con sus requisitos.
 * @property requirements - Lista plana de todos los requisitos del SGSI.
 * @property auditTrail - Historial de cambios registrado en la BD.
 * @property isDemoMode - `true` si se están usando datos mock.
 * @property selectedRequirement - Requisito actualmente seleccionado en la UI.
 * @property metrics - Métricas calculadas en tiempo real por cláusula y globales.
 * @property updateRequirement - Actualiza estado, prioridad o compliance de un requisito.
 * @property assignReferents - Asigna referentes técnicos a un requisito.
 * @property addTask - Agrega una tarea a un requisito específico.
 * @property updateTask - Actualiza una tarea existente.
 * @property addEvidence - Agrega evidencia de cumplimiento a un requisito.
 * @property getRequirementsByClause - Retorna requisitos de una cláusula (con Map memoizado).
 * @property getClauseById - Busca una cláusula por UUID.
 * @property getRequirementById - Busca un requisito por UUID.
 */
interface ISO27001ContextType {
  clauses: ISO27001Clause[];
  requirements: ISO27001Requirement[];
  auditTrail: ISO27001AuditEntry[];
  isLoading: boolean;
  error: string | null;
  isDemoMode: boolean;
  selectedRequirement: ISO27001Requirement | null;
  setSelectedRequirement: (requirement: ISO27001Requirement | null) => void;
  metrics: ISO27001Metrics;
  updateRequirement: (requirementId: string, updates: Partial<ISO27001Requirement>) => Promise<void>;
  assignReferents: (requirementId: string, referentIds: string[]) => Promise<void>;
  addTask: (requirementId: string, task: Omit<ISO27001Task, 'id' | 'requirementId'>) => Promise<void>;
  updateTask: (taskId: string, updates: Partial<ISO27001Task>) => Promise<void>;
  addEvidence: (requirementId: string, evidence: Omit<ISO27001Evidence, 'id' | 'requirementId' | 'createdAt'>) => Promise<void>;
  refreshData: () => Promise<void>;
  getRequirementsByClause: (clauseId: string) => ISO27001Requirement[];
  getClauseById: (clauseId: string) => ISO27001Clause | undefined;
  getRequirementById: (requirementId: string) => ISO27001Requirement | undefined;
}

/** Contexto interno — accesible solo mediante `useISO27001()`. */
const ISO27001Context = createContext<ISO27001ContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

interface ISO27001ProviderProps {
  children: React.ReactNode;
}

/**
 * Proveedor del contexto ISO 27001:2022.
 *
 * Gestiona la carga, actualización y métricas del SGSI. La carga se ejecuta
 * en paralelo (cláusulas + requisitos + auditoría) para maximizar la performance.
 *
 * @param children - Árbol de componentes React a envolver.
 */
export function ISO27001Provider({ children }: ISO27001ProviderProps) {
  const { user } = useAuth();

  // ── Estado ──────────────────────────────────────────────────────────────────
  const [clauses, setClauses] = useState<ISO27001Clause[]>([]);
  const [requirements, setRequirements] = useState<ISO27001Requirement[]>([]);
  const [auditTrail, setAuditTrail] = useState<ISO27001AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState<ISO27001Requirement | null>(null);

  // ─────────────────────────────────────────────────────
  // CARGAR DATOS
  // ─────────────────────────────────────────────────────

  /**
   * Carga cláusulas, requisitos y auditoría desde Supabase en paralelo con `Promise.all`.
   *
   * Manejo especial de errores de tablas inexistentes:
   * - Código PGRST205 (Supabase): relación no encontrada.
   * - Código 42P01 (PostgreSQL): tabla no existe.
   * En ambos casos, se trata como "sin datos" en lugar de error fatal,
   * lo que permite despliegues donde las migraciones ISO 27001 aún no se ejecutaron.
   *
   * Los requisitos incluyen sus asignaciones a referentes mediante join.
   * Se limita el audit trail a los últimos 100 registros para no sobrecargar el cliente.
   *
   * @returns `true` si se cargaron datos; `false` si las tablas no tienen datos o no existen.
   */
  const loadFromDatabase = useCallback(async () => {
    if (!user?.organization_id) {
      logger.warn('[ISO27001] No hay organización del usuario');
      return false;
    }

    try {
      logger.info('[ISO27001] Cargando datos desde BD...');

      // Cargar cláusulas, requisitos y auditoría en paralelo para mejor performance
      const [
        { data: clausesData, error: clausesError },
        { data: requirementsData, error: requirementsError },
        { data: auditData, error: auditError },
      ] = await Promise.all([
        supabase.from('iso27001_clauses').select('*').order('sort_order'),
        supabase.from('iso27001_requirements').select(`
          *,
          iso27001_requirement_assignments (
            referent_id,
            technical_referents (*)
          )
        `).order('code'),
        supabase.from('iso27001_audit_trail').select('*').order('created_at', { ascending: false }).limit(100),
      ]);

      // Detectar si las tablas no existen (migración pendiente) vs errores reales
      // PGRST205: relación no encontrada (Supabase) | 42P01: tabla no existe (PostgreSQL)
      const isTableMissing = (err: { code?: string }) => err?.code === 'PGRST205' || err?.code === '42P01';
      if (clausesError && !isTableMissing(clausesError)) throw clausesError;
      if (requirementsError && !isTableMissing(requirementsError)) throw requirementsError;
      if (auditError && !isTableMissing(auditError)) throw auditError;

      if (clausesData && clausesData.length > 0) {
        // Transformar datos de BD (snake_case) a interfaces TypeScript (camelCase)
        const transformedClauses: ISO27001Clause[] = clausesData.map(c => ({
          id: c.id,
          code: c.code,
          title: c.title,
          description: c.description || '',
          icon: c.icon || 'FileText',
          color: c.color || 'indigo',
          sortOrder: c.sort_order,
          requirements: []
        }));

        const transformedRequirements: ISO27001Requirement[] = (requirementsData || []).map(r => ({
          id: r.id,
          code: r.code,
          title: r.title,
          description: r.description || '',
          guidance: r.guidance,
          clauseId: r.clause_id,
          parentId: r.parent_id,
          status: r.status,
          priority: r.priority,
          implementation: r.implementation,
          dueDate: r.due_date,
          completionDate: r.completion_date,
          compliancePercentage: r.compliance_percentage || 0,
          // Extraer referentes técnicos desde las asignaciones (join anidado)
          assignedReferents: (r.iso27001_requirement_assignments || [])
            .map((a: { technical_referents: TechnicalReferent }) => a.technical_referents)
            .filter(Boolean),
          evidence: [],
          tasks: [],
          children: []
        }));

        const transformedAudit: ISO27001AuditEntry[] = (auditData || []).map(a => ({
          id: a.id,
          entityType: a.entity_type,
          entityId: a.entity_id,
          actionType: a.action_type,
          actionDescription: a.action_description || '',
          userId: a.user_id,
          userName: a.user_name,
          previousValue: a.previous_value,
          newValue: a.new_value,
          createdAt: a.created_at
        }));

        setClauses(transformedClauses);
        setRequirements(transformedRequirements);
        setAuditTrail(transformedAudit);

        logger.info(`[ISO27001] ✅ Cargados ${transformedClauses.length} cláusulas, ${transformedRequirements.length} requisitos`);
        return true;
      }

      return false;
    } catch (err) {
      logger.error('[ISO27001] Error cargando desde BD:', err);
      return false;
    }
  }, [user?.organization_id]);

  /**
   * Carga datos mock de ISO 27001 para el modo demo (sin autenticación).
   * Activa `isDemoMode` para que la UI muestre el banner correspondiente.
   */
  const loadMockData = useCallback(() => {
    logger.info('[ISO27001] 🎭 Cargando datos mock...');

    const clausesWithRequirements = getISO27001ClausesWithRequirements();
    setClauses(clausesWithRequirements);
    setRequirements(mockISO27001Requirements);
    setAuditTrail([]);
    setIsDemoMode(true);

    logger.info(`[ISO27001] ✅ Mock: ${clausesWithRequirements.length} cláusulas, ${mockISO27001Requirements.length} requisitos`);
  }, []);

  /**
   * Orquesta la carga de datos:
   * - Si autenticado: intenta BD; si falla o no hay datos, no usa mock.
   * - Si no autenticado: usa mock directamente (modo demo).
   *
   * Esta lógica garantiza que usuarios autenticados sin tablas ISO 27001
   * vean una UI vacía (no datos de ejemplo de otra organización).
   */
  const refreshData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const loadedFromDb = await loadFromDatabase();

      if (!loadedFromDb && !user?.organization_id) {
        // Solo cargar mock si el usuario no está autenticado (modo demo)
        loadMockData();
      }
    } catch (err) {
      logger.error('[ISO27001] Error general:', err);
      setError('Error al cargar datos ISO 27001');
      if (!user?.organization_id) {
        loadMockData();
      }
    } finally {
      setIsLoading(false);
    }
  }, [loadFromDatabase, loadMockData]);

  /** Carga inicial al montar y recarga cuando cambia el usuario. */
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // ─────────────────────────────────────────────────────
  // MÉTRICAS
  // ─────────────────────────────────────────────────────

  /**
   * Métricas ISO 27001 calculadas en tiempo real desde el estado local.
   *
   * Calcula siempre desde el state (no desde BD), lo que permite ver cambios
   * inmediatos en modo demo y en producción tras actualización optimista.
   *
   * Incluye:
   * - Resumen global (total, completados, en proceso, no iniciados).
   * - Compliance por cláusula (promedio de porcentaje de requisitos).
   * - Requisitos críticos pendientes (prioridad 'critical' sin completar).
   * - Próximos vencimientos (requisitos con dueDate en los próximos 30 días).
   */
  const metrics = React.useMemo((): ISO27001Metrics => {
    // Calcular siempre desde el state actual (válido en demo y producción)
    const allRequirements = requirements;

    const completedRequirements = allRequirements.filter(r => r.status === 'completed').length;
    const inProgressRequirements = allRequirements.filter(r => r.status === 'in_progress').length;
    const notStartedRequirements = allRequirements.filter(r => r.status === 'not_started').length;

    const overallCompliance = allRequirements.length > 0
      ? Math.round(allRequirements.reduce((sum, r) => sum + r.compliancePercentage, 0) / allRequirements.length)
      : 0;

    // Métricas desglosadas por cláusula de la norma
    const byClause = clauses.map(clause => {
      const clauseReqs = allRequirements.filter(r => r.clauseId === clause.id);
      const completed = clauseReqs.filter(r => r.status === 'completed').length;
      const compliance = clauseReqs.length > 0
        ? Math.round(clauseReqs.reduce((sum, r) => sum + r.compliancePercentage, 0) / clauseReqs.length)
        : 0;

      return {
        clauseId: clause.id,
        clauseCode: clause.code,
        clauseTitle: clause.title,
        total: clauseReqs.length,
        completed,
        compliance
      };
    });

    // Requisitos de mayor prioridad aún sin completar (requieren atención urgente)
    const criticalPending = allRequirements.filter(
      r => r.priority === 'critical' && r.status !== 'completed'
    ).length;

    // Requisitos con vencimiento en los próximos 30 días (máximo 5 para el panel)
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    const upcomingDeadlines = allRequirements
      .filter(r => {
        if (!r.dueDate || r.status === 'completed') return false;
        const dueDate = new Date(r.dueDate);
        return dueDate >= today && dueDate <= thirtyDaysFromNow;
      })
      .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
      .slice(0, 5);

    return {
      totalRequirements: allRequirements.length,
      completedRequirements,
      inProgressRequirements,
      notStartedRequirements,
      overallCompliance,
      byClause,
      criticalPending,
      upcomingDeadlines
    };
  }, [requirements, clauses]);

  // ─────────────────────────────────────────────────────
  // ACCIONES CRUD
  // ─────────────────────────────────────────────────────

  /**
   * Actualiza el estado, prioridad, fechas o porcentaje de cumplimiento de un requisito.
   * Usa patrón optimista: actualiza estado local y sincroniza con Supabase.
   * Si la BD falla, revierte recargando desde el servidor.
   *
   * @param requirementId - UUID del requisito a actualizar.
   * @param updates - Campos a modificar del requisito.
   */
  const updateRequirement = useCallback(async (
    requirementId: string,
    updates: Partial<ISO27001Requirement>
  ) => {
    // Actualización optimista del estado local
    setRequirements(prev => prev.map(r =>
      r.id === requirementId ? { ...r, ...updates } : r
    ));

    // Mantener el panel de detalle sincronizado si está abierto
    if (selectedRequirement?.id === requirementId) {
      setSelectedRequirement(prev => prev ? { ...prev, ...updates } : null);
    }

    if (!isDemoMode && user?.organization_id) {
      try {
        const dbUpdates: Record<string, unknown> = {};

        if (updates.status) dbUpdates.status = updates.status;
        if (updates.priority) dbUpdates.priority = updates.priority;
        if (updates.dueDate) dbUpdates.due_date = updates.dueDate;
        if (updates.completionDate) dbUpdates.completion_date = updates.completionDate;
        if (updates.compliancePercentage !== undefined) {
          dbUpdates.compliance_percentage = updates.compliancePercentage;
        }

        const { error } = await supabase
          .from('iso27001_requirements')
          .update(dbUpdates)
          .eq('id', requirementId);

        if (error) throw error;

        logger.info(`[ISO27001] ✅ Requisito ${requirementId} actualizado`);
      } catch (err) {
        logger.error('[ISO27001] Error actualizando requisito:', err);
        // Revertir el cambio optimista
        await refreshData();
      }
    }
  }, [isDemoMode, user?.organization_id, selectedRequirement, refreshData]);

  /**
   * Asigna referentes técnicos a un requisito ISO 27001.
   *
   * Resuelve los UUIDs de referentes a objetos completos (`TechnicalReferent`):
   * - En modo producción: consulta Supabase con join a `technical_referents`.
   * - En modo demo: busca en `mockTechnicalReferents`.
   *
   * La asignación en BD elimina las previas antes de insertar las nuevas.
   *
   * @param requirementId - UUID del requisito al que se asignan los referentes.
   * @param referentIds - Lista de UUIDs de los referentes a asignar.
   */
  const assignReferents = useCallback(async (
    requirementId: string,
    referentIds: string[]
  ) => {
    let referents: TechnicalReferent[];

    if (!isDemoMode && user?.organization_id) {
      try {
        const { data } = await supabase
          .from('technical_referents')
          .select('id, created_at, users(id, name, email, unit, avatar_url, is_active)')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .in('id', referentIds as any);

        // Transformar el resultado del join a tipo TechnicalReferent
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        referents = ((data || []) as any[]).map((r) => ({
          id: r.users?.id || r.id,
          name: r.users?.name || '',
          email: r.users?.email || '',
          role: 'technical_referent' as const,
          unit: r.users?.unit || '',
          avatar: r.users?.avatar_url,
          createdAt: r.created_at,
          isActive: r.users?.is_active || false,
          assignedControls: []
        }));
      } catch {
        // Fallback a mock si la consulta falla
        referents = mockTechnicalReferents.filter(r => referentIds.includes(r.id));
      }
    } else {
      referents = mockTechnicalReferents.filter(r => referentIds.includes(r.id));
    }

    // Actualización optimista del estado local
    setRequirements(prev => prev.map(r =>
      r.id === requirementId ? { ...r, assignedReferents: referents } : r
    ));

    if (selectedRequirement?.id === requirementId) {
      setSelectedRequirement(prev => prev ? { ...prev, assignedReferents: referents } : null);
    }

    if (!isDemoMode && user?.organization_id) {
      try {
        // Eliminar asignaciones anteriores antes de insertar las nuevas
        await supabase
          .from('iso27001_requirement_assignments')
          .delete()
          .eq('requirement_id', requirementId);

        if (referentIds.length > 0) {
          const assignments = referentIds.map(refId => ({
            requirement_id: requirementId,
            referent_id: refId,
            assigned_by: user.id
          }));

          const { error } = await supabase
            .from('iso27001_requirement_assignments')
            .insert(assignments);

          if (error) throw error;
        }

        logger.info(`[ISO27001] ✅ Referentes asignados a ${requirementId}`);
      } catch (err) {
        logger.error('[ISO27001] Error asignando referentes:', err);
        await refreshData();
      }
    }
  }, [isDemoMode, user, selectedRequirement, refreshData]);

  /**
   * Agrega una nueva tarea a un requisito ISO 27001.
   * Genera un ID temporal para la actualización optimista.
   *
   * @param requirementId - UUID del requisito al que se agrega la tarea.
   * @param task - Datos de la tarea (sin `id` ni `requirementId`).
   */
  const addTask = useCallback(async (
    requirementId: string,
    task: Omit<ISO27001Task, 'id' | 'requirementId'>
  ) => {
    const newTask: ISO27001Task = {
      ...task,
      id: `task-${Date.now()}`,
      requirementId
    };

    // Actualización optimista
    setRequirements(prev => prev.map(r =>
      r.id === requirementId
        ? { ...r, tasks: [...r.tasks, newTask] }
        : r
    ));

    if (!isDemoMode && user?.organization_id) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase.from('iso27001_tasks') as any)
          .insert({
            organization_id: user.organization_id,
            requirement_id: requirementId,
            title: task.title,
            description: task.description,
            status: task.status,
            priority: task.priority,
            due_date: task.dueDate,
            checklist: task.checklist
          });

        if (error) throw error;

        logger.info(`[ISO27001] ✅ Tarea creada para ${requirementId}`);
      } catch (err) {
        logger.error('[ISO27001] Error creando tarea:', err);
        await refreshData();
      }
    }
  }, [isDemoMode, user?.organization_id, refreshData]);

  /**
   * Actualiza campos de una tarea existente en cualquier requisito.
   * Recorre todos los requisitos para actualizar la tarea por su ID.
   *
   * @param taskId - UUID de la tarea a actualizar.
   * @param updates - Campos de la tarea a modificar.
   */
  const updateTask = useCallback(async (
    taskId: string,
    updates: Partial<ISO27001Task>
  ) => {
    // Actualización optimista: recorrer todos los requisitos buscando la tarea
    setRequirements(prev => prev.map(r => ({
      ...r,
      tasks: r.tasks.map(t =>
        t.id === taskId ? { ...t, ...updates } : t
      )
    })));

    if (!isDemoMode) {
      try {
        const dbUpdates: Record<string, unknown> = {};
        if (updates.status) dbUpdates.status = updates.status;
        if (updates.title) dbUpdates.title = updates.title;
        if (updates.completedAt) dbUpdates.completed_at = updates.completedAt;

        const { error } = await supabase
          .from('iso27001_tasks')
          .update(dbUpdates)
          .eq('id', taskId);

        if (error) throw error;
      } catch (err) {
        logger.error('[ISO27001] Error actualizando tarea:', err);
        await refreshData();
      }
    }
  }, [isDemoMode, refreshData]);

  /**
   * Agrega una evidencia de cumplimiento a un requisito ISO 27001.
   * Genera timestamps locales y persiste en la tabla `iso27001_evidence`.
   *
   * @param requirementId - UUID del requisito al que se agrega la evidencia.
   * @param evidence - Datos de la evidencia (sin `id`, `requirementId` ni `createdAt`).
   */
  const addEvidence = useCallback(async (
    requirementId: string,
    evidence: Omit<ISO27001Evidence, 'id' | 'requirementId' | 'createdAt'>
  ) => {
    const newEvidence: ISO27001Evidence = {
      ...evidence,
      id: `ev-${Date.now()}`,
      requirementId,
      createdAt: new Date().toISOString()
    };

    // Actualización optimista
    setRequirements(prev => prev.map(r =>
      r.id === requirementId
        ? { ...r, evidence: [...r.evidence, newEvidence] }
        : r
    ));

    if (!isDemoMode && user?.organization_id) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase.from('iso27001_evidence') as any)
          .insert({
            organization_id: user.organization_id,
            requirement_id: requirementId,
            file_name: evidence.fileName,
            file_type: evidence.fileType,
            file_size: evidence.fileSize,
            description: evidence.description,
            uploaded_by: user.id,
            is_verified: evidence.isVerified
          });

        if (error) throw error;

        logger.info(`[ISO27001] ✅ Evidencia agregada a ${requirementId}`);
      } catch (err) {
        logger.error('[ISO27001] Error agregando evidencia:', err);
        await refreshData();
      }
    }
  }, [isDemoMode, user, refreshData]);

  // ─────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────

  /**
   * Mapa precalculado de clauseId → requisitos para lookup O(1).
   * Se recalcula solo cuando cambia la lista de requisitos, evitando
   * que `getRequirementsByClause` itere el array completo en cada render.
   */
  const requirementsByClauseMap = React.useMemo(() => {
    const map = new Map<string, ISO27001Requirement[]>();
    for (const req of requirements) {
      const existing = map.get(req.clauseId) || [];
      existing.push(req);
      map.set(req.clauseId, existing);
    }
    return map;
  }, [requirements]);

  /**
   * Retorna todos los requisitos de una cláusula específica.
   * Usa el mapa precalculado para búsqueda en O(1).
   *
   * @param clauseId - UUID de la cláusula.
   */
  const getRequirementsByClause = useCallback((clauseId: string) => {
    return requirementsByClauseMap.get(clauseId) || [];
  }, [requirementsByClauseMap]);

  /** Busca una cláusula ISO 27001 por su UUID. */
  const getClauseById = useCallback((clauseId: string) => {
    return clauses.find(c => c.id === clauseId);
  }, [clauses]);

  /** Busca un requisito ISO 27001 por su UUID. */
  const getRequirementById = useCallback((requirementId: string) => {
    return requirements.find(r => r.id === requirementId);
  }, [requirements]);

  // ─────────────────────────────────────────────────────
  // VALOR DEL CONTEXTO
  // ─────────────────────────────────────────────────────

  const value: ISO27001ContextType = {
    clauses,
    requirements,
    auditTrail,
    isLoading,
    error,
    isDemoMode,
    selectedRequirement,
    setSelectedRequirement,
    metrics,
    updateRequirement,
    assignReferents,
    addTask,
    updateTask,
    addEvidence,
    refreshData,
    getRequirementsByClause,
    getClauseById,
    getRequirementById
  };

  return (
    <ISO27001Context.Provider value={value}>
      {children}
    </ISO27001Context.Provider>
  );
}

// =====================================================
// HOOK
// =====================================================

/**
 * Hook para consumir el contexto ISO 27001:2022 desde cualquier componente
 * hijo de `ISO27001Provider`.
 *
 * @throws Error si se usa fuera de `ISO27001Provider`.
 * @returns Estado completo y operaciones del SGSI ISO 27001.
 *
 * @example
 * const { requirements, metrics, updateRequirement } = useISO27001();
 * const overallCompliance = metrics.overallCompliance;
 * await updateRequirement(req.id, { status: 'completed', compliancePercentage: 100 });
 */
export function useISO27001(): ISO27001ContextType {
  const context = useContext(ISO27001Context);
  if (context === undefined) {
    throw new Error('useISO27001 debe usarse dentro de un ISO27001Provider');
  }
  return context;
}

export default ISO27001Context;
